/**
 * ActiveFilterChips
 *
 * Displays active filters as removable chips.
 * Each chip shows the filter value with an X button to remove it.
 */

import React from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Text,
  ViewStyle,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import {
  Paprika,
  PaprikaSpacing,
  PaprikaRadius,
} from '../../styles/theme';
import { FilterState } from './FilterSheet';

/** A chip that isn't part of FilterState — e.g. the user's saved diet preference. */
export interface ExtraChip {
  id: string;
  label: string;
  onRemove: () => void;
  dietDot?: string;
}

interface ActiveFilterChipsProps {
  filters: FilterState;
  onRemoveFilter: (update: Partial<FilterState>) => void;
  /** Rendered before the FilterState chips, since a preference outranks a lens. */
  extraChips?: ExtraChip[];
  containerStyle?: ViewStyle;
}

const DIET_DOT_COLORS: Record<'veg' | 'fish' | 'meat', string> = {
  veg: '#5d7c43',
  fish: '#cf6f56',
  meat: Paprika.primary,
};

export const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
  filters,
  onRemoveFilter,
  extraChips,
  containerStyle,
}) => {
  const { t } = useTranslation();

  // Collect all active filter chips
  const chipData: Array<{
    id: string;
    label: string;
    onRemove: () => void;
    dietDot?: string;
  }> = [];

  // Diet chips
  filters.diets.forEach(diet => {
    chipData.push({
      id: `diet-${diet}`,
      label: t(`recipes.filters.diet.${diet}`),
      onRemove: () => {
        onRemoveFilter({
          diets: filters.diets.filter(d => d !== diet),
        });
      },
      dietDot: DIET_DOT_COLORS[diet],
    });
  });

  // Duration chip
  if (filters.maxMinutes !== null) {
    chipData.push({
      id: 'duration',
      label: `< ${filters.maxMinutes} min`,
      onRemove: () => {
        onRemoveFilter({ maxMinutes: null });
      },
    });
  }

  // Rating chip
  if (filters.minRating !== null) {
    chipData.push({
      id: 'rating',
      label: `★ ${filters.minRating.toFixed(1)}+`,
      onRemove: () => {
        onRemoveFilter({ minRating: null });
      },
    });
  }

  // Meal type chips
  filters.mealTypes.forEach(mealType => {
    chipData.push({
      id: `mealType-${mealType}`,
      label: t(`recipes.filters.mealType.${mealType}`),
      onRemove: () => {
        onRemoveFilter({
          mealTypes: filters.mealTypes.filter(m => m !== mealType),
        });
      },
    });
  });

  const allChips = [...(extraChips ?? []), ...chipData];

  // Don't render anything if no filters are active
  if (allChips.length === 0) {
    return null;
  }

  return (
    <View style={[styles.container, containerStyle]}>
      {allChips.map(chip => (
        <ActiveChip
          key={chip.id}
          label={chip.label}
          onRemove={chip.onRemove}
          dietDot={chip.dietDot}
        />
      ))}
    </View>
  );
};

/**
 * Individual active filter chip with X button
 */
interface ActiveChipProps {
  label: string;
  onRemove: () => void;
  dietDot?: string;
}

const ActiveChip: React.FC<ActiveChipProps> = ({ label, onRemove, dietDot }) => {
  return (
    <View style={styles.chip}>
      {/* Diet dot if present */}
      {!!dietDot && (
        <View style={[styles.dietDot, { backgroundColor: dietDot }]} />
      )}

      {/* Label */}
      <Text style={styles.chipLabel}>{label}</Text>

      {/* Remove button */}
      <TouchableOpacity
        style={styles.removeButton}
        onPress={onRemove}
        activeOpacity={0.7}
      >
        <Feather name="x" size={10} color="#fff" />
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.sm,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 13,
    paddingRight: 9,
    paddingVertical: 7,
    borderRadius: PaprikaRadius.full,
    backgroundColor: Paprika.textPrimary,
  },
  dietDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: PaprikaSpacing.xs,
  },
  chipLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    fontWeight: '500',
    color: '#fff',
    marginRight: 6,
  },
  removeButton: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.18)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default ActiveFilterChips;
